import { useState, useEffect, useRef, type ReactElement } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { isAuthEnabled } from '@/env';
import { apiLoginRequest, msalInstance } from '@/lib/auth/msalConfig';
import {
  consumePostLoginReturnTo,
  encodeReturnState,
  sanitizeReturnTo,
  stashReturnTo,
} from '@/lib/auth/returnTo';
import { hasPendingInvite, JOIN_PATH } from '@/lib/invite/inviteSession';
import { MicrosoftLogo } from '@/components/MicrosoftLogo';
import { Wordmark } from '@/components/Wordmark';

// Phase 5.X-screen-login — V1 parity
//
// V1 LoginScreen.jsx had:
//   1. A horizontal divider below the tagline
//   2. "Sign in with your organization's Microsoft 365 account" sub-text
//   3. An error banner (.login-error) when loginRedirect throws
//   4. The footer OUTSIDE the card (dark background → white text via .login-footer)
//
// V2 previously had (2) and (4) missing, and (3) not wired.

export function LoginPage(): ReactElement {
  const isAuthed = useIsAuthenticated();
  const { inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  // V1 parity: surface loginRedirect errors inline.
  const [authError, setAuthError] = useState<string | null>(null);
  const resolvedTarget = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthEnabled()) {
      navigate('/home', { replace: true });
      return;
    }
    if (isAuthed && inProgress === 'none') {
      // T-17 (T17-D13) + REVIEW #2 — resolve the post-login destination, most
      // reliable signal first:
      //   1. the post-login stash (nonce round trip through sessionStorage) —
      //      the only signal that survives `loginRedirect` leaving the SPA and
      //      returning to `/`, where in-memory router state is already gone;
      //   2. the router-state `from`, enough when this page is reached while
      //      already signed in;
      //   3. an invitation still waiting in THIS tab — a durable signal whose
      //      path (JOIN_PATH) is a constant, so /join's promise to bring the
      //      invited person back holds even after a real sign-in;
      //   4. /home.
      // (1) and (2) pass the one sanitiser, so a hostile value falls through;
      // (3) reads nothing from the query string, so it cannot become an open
      // redirect. The stash is read-once, so the resolved target is memoised in
      // a ref: StrictMode's double effect must not turn the second run into a
      // different destination.
      if (resolvedTarget.current === null) {
        resolvedTarget.current =
          consumePostLoginReturnTo() ??
          sanitizeReturnTo((location.state as { from?: unknown } | null)?.from) ??
          (hasPendingInvite() ? JOIN_PATH : null) ??
          '/home';
      }
      navigate(resolvedTarget.current, { replace: true });
    }
  }, [isAuthed, inProgress, navigate, location.state]);

  // V1 parity: catch loginRedirect errors and surface them. No MSAL config
  // is changed — this is error-display wiring only.
  const handleSignIn = (): void => {
    setAuthError(null);
    // T-17 (T17-D13) — the path is parked in sessionStorage; only an opaque
    // nonce rides in the MSAL `state`. Nothing valid to stash → plain request.
    const nonce = stashReturnTo(
      sanitizeReturnTo((location.state as { from?: unknown } | null)?.from) ?? '',
    );
    const request =
      nonce !== null ? { ...apiLoginRequest, state: encodeReturnState(nonce) } : apiLoginRequest;
    msalInstance.loginRedirect(request).catch((err: unknown) => {
      const e = err as { errorCode?: string; errorMessage?: string; message?: string };
      if (e.errorCode === 'user_cancelled') return;
      if (e.errorMessage?.includes('redirect')) {
        setAuthError(
          'Azure redirect URI mismatch. Contact your administrator to add this URL to the Azure app registration: ' +
            window.location.origin,
        );
      } else {
        setAuthError(e.errorMessage ?? e.message ?? 'Sign-in failed. Please try again.');
      }
    });
  };

  const disabled = inProgress !== 'none' || !isAuthEnabled();

  return (
    <main className="login-page">
      <div className="login-card">
        <h1 className="login-logo">
          <Wordmark />
        </h1>
        <p className="login-tagline">Regulatory Submission Platform</p>
        {/* V1 parity: divider between tagline and sign-in copy */}
        <div className="login-divider" />
        {/* V1 parity: sub-text above the button */}
        <p
          data-testid="login-org-copy"
          style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}
        >
          Sign in with your organization&apos;s Microsoft 365 account
        </p>
        {/* V1 parity: inline error banner for auth failures */}
        {authError && (
          <div
            className="login-error"
            role="alert"
            data-testid="login-error"
          >
            {authError}
          </div>
        )}
        <button
          type="button"
          className="btn-microsoft"
          onClick={handleSignIn}
          disabled={disabled}
        >
          <MicrosoftLogo />
          {inProgress !== 'none' ? 'Signing in…' : 'Sign in with Microsoft'}
        </button>
        {!isAuthEnabled() && (
          <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text-muted)' }}>
            Auth is disabled in this environment. Redirecting to home…
          </div>
        )}
      </div>
      {/* V1 parity: footer outside the card so it renders on the dark
          gradient background with white text (.login-footer CSS). */}
      <div className="login-footer">
        Powered by Microsoft Entra ID &middot; Multi-tenant SSO
      </div>
    </main>
  );
}
